import React, { useMemo } from 'react';

import { StarFilled } from '@ant-design/icons';
import { Scatter } from '@ant-design/plots';
import { renderToStaticMarkup } from 'react-dom/server';
import styled from 'styled-components';

import { AGGREGATION_TYPE, AGGREGATION_TYPE_LABELS } from 'components/View/constants';
import { useAppSelector } from 'store/hook';
import { formatScore } from 'utils/helpers/numbers';

import {
  MARKET_POSITIONING_APPEND_PADDING,
  MARKET_POSITIONING_AXIS_TITLE_COLOR,
  MARKET_POSITIONING_BASELINE_COLOR,
  MARKET_POSITIONING_FALLBACK_QUALITY_MAX,
  MARKET_POSITIONING_FALLBACK_QUALITY_MIN,
  MARKET_POSITIONING_FALLBACK_RATE_MAX,
  MARKET_POSITIONING_FALLBACK_RATE_MIN,
  MARKET_POSITIONING_GRID_LINE_COLOR,
  MARKET_POSITIONING_LABEL_GUTTER,
  MARKET_POSITIONING_POINT_LABEL_MAX_WIDTH,
  MARKET_POSITIONING_POINT_LABEL_OFFSET_Y,
  MARKET_POSITIONING_POINT_SIZE,
  MARKET_POSITIONING_QUALITY_PADDING_FALLBACK,
  MARKET_POSITIONING_QUALITY_PADDING_RATIO,
  MARKET_POSITIONING_RATE_MAX_MULTIPLIER,
  MARKET_POSITIONING_RATE_MIN_MULTIPLIER,
  MARKET_POSITIONING_TOOLTIP_TEXT_COLOR,
} from './constants';
import { MarketPositioningPoint } from './types';
import { WIDGET_TYPES } from '../constants';
import { labelFormatter } from '../Graph/utils';
import { calculateValueDifference, formatValueDifference, getDifferenceColor } from '../valueDifferenceUtils';

type Props = {
  data: MarketPositioningPoint[];
  showDifference?: boolean;
  valueDisplay?: number;
  community?: MarketPositioningPoint;
  roomTypesPriceDisplay?: number;
  xAxisTitle: string;
  yAxisTitle?: string;
  useScoreFormat?: boolean;
  xAxisRange?: { min: number; max: number };
  yAxisRange?: { min: number; max: number };
  xAxisLabel?: string;
  yAxisLabel?: string;
  xAxisFormatter?: (value: string) => string;
  yAxisFormatter?: (value: string) => string;
  xTooltipFormatter?: (point: MarketPositioningPoint) => string;
  yTooltipFormatter?: (point: MarketPositioningPoint) => string;
};

const ChartContainer = styled.div`
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  min-height: 280px;
  height: 100%;
  padding: 10px 0;

  .g2-tooltip {
    background: none !important;
    border: none !important;
    box-shadow: none !important;
    padding: 0 !important;
  }
`;

const MarketPositioningChart = ({
  data,
  showDifference,
  valueDisplay,
  community: baseline,
  roomTypesPriceDisplay,
  xAxisTitle,
  yAxisTitle = 'Market Rate',
  useScoreFormat,
  xAxisRange,
  yAxisRange,
  xAxisLabel = 'Your Score',
  yAxisLabel = 'Your Market Rate',
  xAxisFormatter = (value) => Number(value).toFixed(1),
  yAxisFormatter = (value) => labelFormatter(parseFloat(value), WIDGET_TYPES.MARKET_RATE_GRAPH),
  xTooltipFormatter,
  yTooltipFormatter,
}: Props) => {
  const { selectedCommunity } = useAppSelector((state) => state.global);
  const selectedCommunityId = selectedCommunity?.id;
  const myCommunityPoint = useMemo(
    () => baseline ?? data.find((point) => point.community_id === selectedCommunityId),
    [baseline, data, selectedCommunityId]
  );
  const qualityBaseline = myCommunityPoint?.quality_score ?? null;
  const rateBaseline = myCommunityPoint?.market_rate ?? null;

  const { minQuality, maxQuality, minRate, maxRate } = useMemo(() => {
    if (!data.length) {
      return {
        minQuality: MARKET_POSITIONING_FALLBACK_QUALITY_MIN,
        maxQuality: MARKET_POSITIONING_FALLBACK_QUALITY_MAX,
        minRate: MARKET_POSITIONING_FALLBACK_RATE_MIN,
        maxRate: MARKET_POSITIONING_FALLBACK_RATE_MAX,
      };
    }

    const qualities = data.map((point) => point.quality_score);
    const rates = data.map((point) => point.market_rate);
    const min = Math.min(...qualities);
    const max = Math.max(...qualities);
    const padding =
      (max - min) * MARKET_POSITIONING_QUALITY_PADDING_RATIO || MARKET_POSITIONING_QUALITY_PADDING_FALLBACK;
    const paddingMin = min - padding;
    const paddingMax = max + padding;

    return {
      minQuality: xAxisRange?.min ?? paddingMin,
      maxQuality: xAxisRange?.max ?? paddingMax,
      minRate:
        yAxisRange?.min ??
        Math.max(MARKET_POSITIONING_FALLBACK_RATE_MIN, Math.min(...rates) * MARKET_POSITIONING_RATE_MIN_MULTIPLIER),
      maxRate: yAxisRange?.max ?? Math.max(...rates) * MARKET_POSITIONING_RATE_MAX_MULTIPLIER,
    };
  }, [data, xAxisRange, yAxisRange]);

  const aggregationLabel = (
    AGGREGATION_TYPE_LABELS[roomTypesPriceDisplay ?? AGGREGATION_TYPE.AVG] ??
    AGGREGATION_TYPE_LABELS[AGGREGATION_TYPE.AVG]
  ).toLowerCase();

  const pointLabelAnnotations =
    data?.map((point) => ({
      type: 'html' as const,
      position: [point.quality_score, point.market_rate],
      html: () => `
        <div
          title="${point.community_name}"
          style="
            transform: translate(-50%, ${MARKET_POSITIONING_POINT_LABEL_OFFSET_Y}px);
            color: var(--text-secondary);
            background-color: white;
            font-size: 12px;
            font-family: var(--font-medium);
            border:1px solid rgba(99, 110, 132, 0.35);
            padding: 1px 10px;

            display: inline-block;
            max-width: ${MARKET_POSITIONING_POINT_LABEL_MAX_WIDTH}px;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
            pointer-events: none;
          "
        >
          ${point.community_name}
        </div>
      `,
    })) ?? [];

  const config = {
    data,
    autoFit: true,
    xField: 'quality_score',
    yField: 'market_rate',
    shape: 'circle',
    size: MARKET_POSITIONING_POINT_SIZE,
    colorField: 'community_color',
    color: (communityColor: string | undefined) => communityColor,
    pointStyle: (point: MarketPositioningPoint) => ({ fill: point?.community_color }),
    label: false as const,
    appendPadding: MARKET_POSITIONING_APPEND_PADDING,
    xAxis: {
      min: minQuality,
      max: maxQuality,
      label: { formatter: xAxisFormatter },
      grid: {
        line: {
          style: {
            stroke: MARKET_POSITIONING_GRID_LINE_COLOR,
          },
        },
      },
      title: {
        text: xAxisTitle,
        style: {
          fontSize: 14,
          color: MARKET_POSITIONING_AXIS_TITLE_COLOR,
          fontFamily: 'AvenirLTPro-Heavy', // antd plot doesnt pass var(--font-bold) properly, thus we have to hardcode
        },
      },
      nice: false,
      tickCount: 6,
    },
    yAxis: {
      min: minRate,
      max: maxRate,
      label: { formatter: yAxisFormatter },
      grid: {
        line: {
          style: {
            stroke: MARKET_POSITIONING_GRID_LINE_COLOR,
          },
        },
      },
      title: {
        text: yAxisTitle,
        style: {
          fontSize: 14,
          color: MARKET_POSITIONING_AXIS_TITLE_COLOR,
          fontFamily: 'AvenirLTPro-Heavy',
        },
      },
    },
    tooltip: {
      showMarkers: false,
      customContent: (title: string, items: any[]) => {
        const point = items?.[0]?.data as MarketPositioningPoint | undefined;
        if (!point) {
          return '';
        }
        const isCompetitor = baseline && point.community_id !== baseline.community_id;
        const buildDiffHtml = (base: number, target: number, widgetType: number) => {
          if (!showDifference || !isCompetitor || !baseline) return '';
          const diff = calculateValueDifference(base, target, valueDisplay, widgetType);
          const formatted = formatValueDifference(diff, widgetType, valueDisplay);
          if (!formatted) return '';
          const color = getDifferenceColor(diff!);
          return `<span style="color:${color};margin-left:4px;">${formatted}</span>`;
        };
        const xTooltipValue = xTooltipFormatter
          ? xTooltipFormatter(point)
          : `${formatScore(point.quality_score)}${buildDiffHtml(
              baseline?.quality_score ?? 0,
              point.quality_score,
              WIDGET_TYPES.RATING_GRAPH
            )}`;
        const yTooltipValue = yTooltipFormatter
          ? yTooltipFormatter(point)
          : `$${Math.round(point.market_rate).toLocaleString()} ${aggregationLabel}${buildDiffHtml(
              baseline?.market_rate ?? 0,
              point.market_rate,
              WIDGET_TYPES.MARKET_RATE_GRAPH
            )}`;
        return `
          <div
            style="
              padding:10px 14px;
              min-width:160px;
              color:${MARKET_POSITIONING_TOOLTIP_TEXT_COLOR};
              font-family:var(--font-regular);
              border-radius:12px;
              border:1px solid rgba(255,255,255,0.6);
              background: linear-gradient(135deg, var(--community-marker-purple-dark), var(--community-marker-purple-light));
              box-shadow:0 10px 30px rgba(31, 33, 44, 0.35);
            "
          >
            <div style="font-size:13px;font-family:var(--font-bold);margin-bottom:6px;">${point.community_name}</div>
            <div style="display:flex;align-items:center;gap:6px;font-size:12px;margin-bottom:4px;">
              ${useScoreFormat ? '' : renderToStaticMarkup(<StarFilled style={{ color: MARKET_POSITIONING_TOOLTIP_TEXT_COLOR, fontSize: 12 }} />)}
              ${xTooltipValue}
            </div>
            <div style="font-size:12px;font-family:var(--font-medium);">
              ${yTooltipValue}
            </div>
          </div>
        `;
      },
    },
    legend: false as const,
    annotations: [
      ...pointLabelAnnotations,
      ...(qualityBaseline !== null
        ? [
            {
              type: 'line',
              start: [qualityBaseline, 'min'],
              end: [qualityBaseline, 'max'],
              style: {
                stroke: MARKET_POSITIONING_BASELINE_COLOR,
                lineDash: [6, 6],
                lineWidth: 1,
              },
            },
            {
              type: 'html',
              position: [qualityBaseline, 'max'],
              html: () => `
                <div
                  style="
                    color:${MARKET_POSITIONING_BASELINE_COLOR};
                    font-size:12px;
                    font-weight:600;
                    white-space:nowrap;
                    transform: translate(-50%, -100%) translateY(-${MARKET_POSITIONING_LABEL_GUTTER}px);
                    pointer-events:none;
                  "
                >
                  ${xAxisLabel}
                </div>
              `,
            },
          ]
        : []),
      ...(rateBaseline !== null
        ? [
            {
              type: 'line',
              start: ['min', rateBaseline],
              end: ['max', rateBaseline],
              style: {
                stroke: MARKET_POSITIONING_BASELINE_COLOR,
                lineDash: [6, 6],
                lineWidth: 1,
              },
            },
            {
              type: 'html',
              position: ['max', rateBaseline],
              html: () => `
                <div
                  style="
                    color:${MARKET_POSITIONING_BASELINE_COLOR};
                    font-size:12px;
                    font-weight:600;
                    white-space:nowrap;
                    transform: translate(${MARKET_POSITIONING_LABEL_GUTTER}px, 0) rotate(270deg) translateX(-50%);
                    transform-origin: left top;
                    pointer-events:none;
                  "
                >
                  ${yAxisLabel}
                </div>
              `,
            },
          ]
        : []),
    ],
  };

  return (
    <ChartContainer>
      <Scatter {...(config as any)} />
    </ChartContainer>
  );
};

export default MarketPositioningChart;
